import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Empty, Skeleton } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import CustomLabelDropdown from "../../../components/shared/new Form/Fields/CustomLabelDropdown";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomInput from "../../../components/shared/Input/Custom-Input";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import CommonTable from "../../../components/shared/table/Table";
import { courseSingleColumns, commonColumns } from "../../../assets/FormFeilds/CourseSingleReportField";
import { useSingleCourseReport } from "../../../hooks/CoursesHooks/useSingleCourseReport";
import searchIconsvg from "../../../assets/Search-Icon.svg";
import { showErrorMessage, showSuccessMessage } from "../../../components/shared/pagination/messages";
import { STATUS_FILTER_OPTIONS, FILTER_TYPE_OPTIONS, SUB_TYPE_OPTIONS } from "../../../assets/constants/admin/CoursesConstants.js";
import { downloadSingleKpiReport, filterOptions } from "../../../service/AdminService/courseService";
import usePagination from "../../../assets/hooks/PaginationHooks/usePagination.jsx";
import PageLoader from "../../../components/global/Loader/PageLoader.jsx";
import backButton from "../../../assets/back-btn.svg";
import { debounceFunction } from "../../../assets/commonFunctions/CommonFunction.jsx";

const CourseSingleReport = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const tableRef = useRef(null);

    const { data, fetchSingleCourseReport } = useSingleCourseReport();
    const [loading, setLoading] = useState(false);
    const [loader, setLoader] = useState(false);
    const [filterloading, setFilterLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [isSearchHovered, setIsSearchHovered] = useState(false);
    const [courseName, setCourseName] = useState("N/A");
    const [searchInput, setSearchInput] = useState("");

    const [filters, setFilters] = useState({
        type: null,
        subType: null,
        groupId: null,
        bundleId: null,
        userId: null,
        status: null,
    });

    const [apiFilterOptions, setApiFilterOptions] = useState({
        groups: [],
        bundles: [],
        users: [],
    });

    const {
        currentPage, setCurrentPage, totalElements, setTotalElements,
        pageSize, setPageSize,
    } = usePagination();

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchSingleCourseReport(courseId, {
                page: currentPage - 1,
                size: pageSize,
                search: searchText || undefined,
                filterType: filters.type,
                subType: filters.subType,
                groupId: filters.groupId || undefined,
                bundleId: filters.bundleId || undefined,
                userId: filters.userId || undefined,
                status: filters.status || undefined,
            });
            if (response) {
                setTotalElements(response.totalElements);
            }
        } catch (error) {
            showErrorMessage("Failed to Load Data.");
        } finally {
            setLoading(false);
        }
    }, [courseId, currentPage, pageSize, filters, searchText, fetchSingleCourseReport, setTotalElements]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const loadFilterOptions = useCallback(async () => {
        if (!courseId) return;
        setFilterLoading(true);
        try {
            const res = await filterOptions(courseId);
            setApiFilterOptions(res);
            if (res?.courseDetails?.name) {
                setCourseName(res.courseDetails.name);
            }
        } catch {
            showErrorMessage("Failed to load filter options");
        } finally {
            setFilterLoading(false);
        }
    }, [courseId]);

    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    const debouncedSearch = useMemo(
        () => debounceFunction((value) => {
            setSearchText(value);
            setCurrentPage(1);
        }, 500),
        []
    );

    const handleSearch = useCallback((value) => {
        setSearchInput(value);
        debouncedSearch(value.toLowerCase().trim());
    }, [debouncedSearch]);

    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    }, [setCurrentPage]);

    const handleTypeChange = useCallback((value) => {
        setFilters((prev) => ({ ...prev, type: value ?? null, bundleId: null }));
        setCurrentPage(1);
    }, [setCurrentPage]);

    const handleSubTypeChange = useCallback((value) => {
        setFilters((prev) => ({ ...prev, subType: value ?? null, groupId: null, userId: null }));
        setCurrentPage(1);
    }, [setCurrentPage]);

    const groupDropdownOptions = useMemo(() =>
        apiFilterOptions.groups.map((g) => ({ value: g.id, label: g.name })),
        [apiFilterOptions.groups]);

    const bundleDropdownOptions = useMemo(() =>
        apiFilterOptions.bundles.map((b) => ({ value: b.id, label: b.name })),
        [apiFilterOptions.bundles]);

    const userDropdownOptions = useMemo(() =>
        apiFilterOptions.users.map((u) => ({ value: u.id, label: u.name })),
        [apiFilterOptions.users]);

    const tableData = useMemo(() => {
        if (!data?.content?.length) return [];
        return data.content.slice(0, pageSize).map((item, index) => {
            if (item.type === "GROUP") {
                return {
                    ...item,
                    key: `group-${item.groupId}-${item.enrollmentSource}-${index}`,
                    displayTitle: item.groupName,
                    enrolleeType: "GROUP",
                    courseCompletionPercentage: item.avgProgress,
                    status: item.groupStatus,
                    adherence: item.worstAdherence,
                    enrolledAt: item.assignedAt,
                };
            }
            return {
                ...item,
                key: `user-${item.enrollmentId}-${index}`,
                displayTitle: item.fullName,
                enrolleeType: "USER",
                enrolledAt: item.assignedAt,
            };
        });
    }, [data, pageSize]);

    const downloadReport = async (id) => {
        setLoader(true);
        try {
            await downloadSingleKpiReport(id);
            showSuccessMessage("Report Downloaded Successfully!");
        } catch (error) {
            showErrorMessage("Failed to download report");
        } finally {
            setLoader(false);
        }
    };

    const renderExpandedRow = (record) => {
        if (record.enrolleeType === "GROUP") {
            if (record.members && record.members.length > 0) {
                return (
                    <CommonTable
                        data={record.members.map((u) => ({
                            ...u,
                            displayTitle: u.fullName,
                            enrolleeType: "USER",
                            enrolledAt: record.assignedAt,
                        }))}
                        columns={courseSingleColumns}
                        pagination={false}
                        serverSide={false}
                    />
                );
            }
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        if (record.enrolleeType === "USER") {
            return (
                <div className="p-4 flex justify-center bg-[#fafafa] border-t border-b border-[#f0f0f0]">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
                </div>
            );
        }

        return null;
    };

    const handleTableChange = useCallback((pagination) => {
        if (!pagination) return;
        if (pagination.pageSize !== pageSize) {
            setPageSize(pagination.pageSize);
            setCurrentPage(1);
        } else {
            setCurrentPage(pagination.current);
        }
        const scrollableNode = tableRef.current?.querySelector('.ant-table-body');
        if (scrollableNode) scrollableNode.scrollTo({ top: 0, behavior: 'auto' });
    }, [pageSize, setPageSize, setCurrentPage]);

    return (
        <>
            <PageLoader visible={loader} />
            <section className="w-[100%] max-w-full h-[90vh] p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)]">
                <header className="flex flex-col gap-6 mb-6">
                    <div className="flex justify-between items-center h-[50px]">
                        <h1 className="text-[#E74860] text-[1.75rem] font-normal">
                            {!filterloading ? (
                                `${courseName} Report`
                            ) : (
                                <Skeleton.Input active style={{ width: 220, height: 28, borderRadius: 6 }} />
                            )}
                        </h1>
                        <div className="flex items-center gap-4">
                            <span className="font-medium">Total Enrollments: {totalElements ?? 0}</span>
                            <div
                                className="flex gap-2 justify-center cursor-pointer"
                                onClick={() => navigate(-1)}
                            >
                                <img alt="back-btn" src={backButton} />
                                <span>Back</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-center h-[46px] gap-4">
                        <div
                            onMouseEnter={() => setIsSearchHovered(true)}
                            onMouseLeave={() => setIsSearchHovered(false)}
                        >
                            <CustomInput
                                icon={
                                    isSearchHovered
                                        ? <LottieIcon name="searchIcon" size={20} shouldPlay={true} />
                                        : <img src={searchIconsvg} alt="search" style={{ width: 20, height: 20 }} />
                                }
                                style={{ width: "12vw" }}
                                value={searchInput}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Search by Name"
                                allowClear
                            />
                        </div>
                        <div className="flex justify-between items-end h-[46px] gap-4">
                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.type}
                                    onChange={handleTypeChange}
                                    options={FILTER_TYPE_OPTIONS}
                                    placeholder="All Enrollments"
                                />
                            </div>

                            {filters.type === "BUNDLE" && (
                                <div style={{ width: "11vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.bundleId}
                                        onChange={(v) => setFilter("bundleId", v ?? null)}
                                        options={bundleDropdownOptions}
                                        placeholder="Select Curriculum"
                                    />
                                </div>
                            )}

                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.subType}
                                    onChange={handleSubTypeChange}
                                    options={SUB_TYPE_OPTIONS}
                                    placeholder="All Types"
                                />
                            </div>

                            {filters.subType === "GROUP" && (
                                <div style={{ width: "10vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.groupId}
                                        onChange={(v) => setFilter("groupId", v ?? null)}
                                        options={groupDropdownOptions}
                                        placeholder="Select Group"
                                    />
                                </div>
                            )}

                            {filters.subType === "USER" && (
                                <div style={{ width: "10vw" }}>
                                    <CustomLabelDropdown
                                        value={filters.userId}
                                        onChange={(v) => setFilter("userId", v ?? null)}
                                        options={userDropdownOptions}
                                        placeholder="Select User"
                                        showSearch
                                    />
                                </div>
                            )}

                            <div style={{ width: "10vw" }}>
                                <CustomLabelDropdown
                                    value={filters.status}
                                    onChange={(v) => setFilter("status", v ?? null)}
                                    options={STATUS_FILTER_OPTIONS}
                                    placeholder="All Status"
                                />
                            </div>

                            <CustomButton
                                onClick={() => downloadReport(courseId)}
                                className="add-btn"
                                loading={loader}
                            >
                                Export
                            </CustomButton>

                            <CustomButton
                                onClick={() => navigate(`/quiz-report?courseId=${courseId}&courseName=${encodeURIComponent(data?.courseTitle || '')}`)}
                                className="add-btn"
                            >
                                Quiz Reports
                            </CustomButton>
                        </div>
                    </div>
                </header>

                <div ref={tableRef}>
                    <CommonTable
                        data={tableData}
                        columns={commonColumns}
                        serverSide
                        rowKey="key"
                        showLoader={loading}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalElements,
                        }}
                        onChange={handleTableChange}
                        scroll={{ y: "60vh" }}
                        className="hide-scrollbar"
                        expandedRowRender={renderExpandedRow}
                        rowExpandable={(record) =>
                            record.enrolleeType === "GROUP" 
                        }
                    />
                </div>
            </section>
        </>
    );
};

export default AdminHOC(CourseSingleReport);